/* alpine-context-cta.js — task #258
   Behaviour for the site-wide Call / Request bar (#alp-cta). No dependencies.

   Reads its settings off the bar's data attributes:
     data-context      rentals | parts | service   (for GA4)
     data-threshold    viewports scrolled before it shows (default 0.8)
     data-dismiss      "0" hides the X and disables session dismiss
     data-preview-only "1" shows the bar only when the URL carries ?cta=preview

   Rules:
     - shows once the visitor has scrolled past threshold × viewport height
     - hides while any [data-cta-hide] element (forms, footer) is in view
       (checked from element rects on each scroll frame)
     - on desktop (≥750px) shows only while the theme header carries
       .shopify-section-header-hidden, so strip and header never stack
     - X hides it for the session (sessionStorage)
     - GA4: context_cta { context, cta: call|request|dismiss, device }

   window.AlpContextCta.configure({threshold, dismiss, style, subline, reset})
   exists for the design preview; production never calls it. */
(function () {
  'use strict';

  var bar = document.getElementById('alp-cta');
  if (!bar) return;

  var KEY = 'alp-cta-dismissed';
  var desktop = window.matchMedia('(min-width: 750px)');
  var header = document.querySelector('.section-header');
  var cfg = { threshold: 0.8, dismiss: true, previewOnly: false };
  var state = { armed: false, headerHidden: false, dismissed: false };
  var zones = [];

  function readConfig() {
    var t = parseFloat(bar.getAttribute('data-threshold'));
    cfg.threshold = isNaN(t) ? 0.8 : t;
    cfg.dismiss = bar.getAttribute('data-dismiss') !== '0';
    cfg.previewOnly = bar.getAttribute('data-preview-only') === '1';
    bar.classList.toggle('alp-cta--no-dismiss', !cfg.dismiss);
  }

  function storedDismiss() {
    try { return !!window.sessionStorage.getItem(KEY); } catch (e) { return false; }
  }

  function track(cta) {
    if (typeof window.gtag !== 'function') return;
    window.gtag('event', 'context_cta', {
      context: bar.getAttribute('data-context') || 'unknown',
      cta: cta,
      device: desktop.matches ? 'desktop' : 'mobile'
    });
  }

  function scrolledPast() {
    var y = window.pageYOffset || document.documentElement.scrollTop || 0;
    return y > window.innerHeight * cfg.threshold;
  }

  // A hide zone counts as "in view" when any part of it overlaps the viewport.
  // Computed from rects on demand (a handful of elements) rather than held in
  // IntersectionObserver state, which browsers deliver late in throttled frames.
  function blocked() {
    var h = window.innerHeight;
    for (var i = 0; i < zones.length; i++) {
      var r = zones[i].getBoundingClientRect();
      if (r.height > 0 && r.bottom > 0 && r.top < h) return true;
    }
    return false;
  }

  // The Shopify Forms popup teaser ("Parts Help") is a fixed #app-embed inside
  // an open shadow root at z-index 2147483647, bottom-right, and it covered the
  // Request button on phones. Page CSS cannot reach into the shadow root, but a
  // stylesheet appended to it can key off :host(), so one injected rule plus a
  // host attribute lifts the teaser above the bar while the bar is showing.
  function liftFormsTeaser(lift) {
    var hosts = document.querySelectorAll('shopify-forms-embed');
    for (var i = 0; i < hosts.length; i++) {
      var h = hosts[i], sr = h.shadowRoot;
      if (!sr) continue;
      if (!sr.querySelector('style[data-alp-cta]')) {
        var st = document.createElement('style');
        st.setAttribute('data-alp-cta', '');
        st.textContent = '@media (max-width:749px){:host([data-alp-lifted]) #app-embed{bottom:calc(66px + env(safe-area-inset-bottom)) !important;transition:bottom .28s cubic-bezier(.25,1,.5,1)}}';
        sr.appendChild(st);
      }
      if (lift) h.setAttribute('data-alp-lifted', ''); else h.removeAttribute('data-alp-lifted');
    }
  }

  function update() {
    var show = state.armed && !state.dismissed && scrolledPast() && !blocked() &&
      (!desktop.matches || state.headerHidden);
    // visibility:hidden in the CSS keeps the hidden bar out of the tab order,
    // so no aria-hidden juggling is needed here.
    bar.classList.toggle('is-visible', show);
    liftFormsTeaser(show && !desktop.matches);
  }

  var ticking = false;
  function onScroll() {
    if (ticking) return;
    ticking = true;
    window.requestAnimationFrame(function () { ticking = false; update(); });
  }

  function findHideZones() {
    zones = Array.prototype.slice.call(document.querySelectorAll(
      '[data-cta-hide], footer.footer, .shopify-app-block[id*="forms_inline"], form[action^="/contact"]'
    ));
  }

  function watchHeader() {
    // Fails closed: no header found → desktop strip never shows; mobile unaffected.
    if (!header || !('MutationObserver' in window)) return;
    var read = function () {
      var h = header.classList.contains('shopify-section-header-hidden');
      if (h !== state.headerHidden) { state.headerHidden = h; update(); }
    };
    new MutationObserver(read).observe(header, { attributes: true, attributeFilter: ['class'] });
    read();
  }

  function wireClicks() {
    bar.addEventListener('click', function (ev) {
      var el = ev.target.closest ? ev.target.closest('[data-cta]') : null;
      if (!el) return;
      var cta = el.getAttribute('data-cta');
      if (cta === 'dismiss') {
        ev.preventDefault();
        state.dismissed = true;
        try { window.sessionStorage.setItem(KEY, '1'); } catch (e) { /* private mode */ }
        update();
      }
      track(cta);
    });
  }

  function arm() {
    readConfig();
    // The #254 rental-hub section carries its own bar on the rental collections
    // until its verdict (2026-10-21). Defer to it wherever it renders.
    if (document.querySelector('.rhub-sticky')) return;
    if (cfg.previewOnly && !/[?&]cta=preview(&|$)/.test(window.location.search)) return;
    state.dismissed = cfg.dismiss && storedDismiss();
    state.armed = true;
    findHideZones();
    window.addEventListener('scroll', onScroll, { passive: true });
    window.addEventListener('resize', onScroll, { passive: true });
    window.addEventListener('load', function () { findHideZones(); update(); });
    desktop.addEventListener ? desktop.addEventListener('change', update) : desktop.addListener(update);
    watchHeader();
    wireClicks();
    // Safety net for content that changes height without a scroll (lazy blocks).
    window.setInterval(update, 1500);
    update();
  }

  window.AlpContextCta = {
    configure: function (o) {
      o = o || {};
      if (o.threshold != null) bar.setAttribute('data-threshold', String(o.threshold));
      if (o.dismiss != null) bar.setAttribute('data-dismiss', o.dismiss ? '1' : '0');
      if (o.style) bar.classList.toggle('alp-cta--slate', o.style === 'slate');
      if (typeof o.subline === 'string') {
        var s = bar.querySelector('.alp-cta__line span');
        if (s) s.textContent = o.subline;
      }
      if (o.reset) {
        try { window.sessionStorage.removeItem(KEY); } catch (e) { /* ignore */ }
        state.dismissed = false;
      }
      readConfig();
      update();
    }
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', arm);
  } else {
    arm();
  }
})();
